import React, { useState } from "react";
import api from "../../api/api";
import "./ManageSubscriptionModal.css";

const ManageSubscriptionModal = ({ isOpen, onClose, subscription, onUpdate }) => {
  const [step, setStep] = useState(1); // 1: Info, 2: Survey, 3: Final Warning
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  if (!isOpen) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleCancelClick = async () => {
    setLoading(true);
    try {
      await api.post("/billing/cancel", { reason });
      setCancelled(true);
      if (onUpdate) onUpdate();
    } catch (err) {
      alert("Failed to cancel subscription. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  const SURVEY_OPTIONS = [
    { id: "expensive", label: "It's too expensive" },
    { id: "not_useful", label: "I don't find it useful" },
    { id: "exploring", label: "I was just exploring" },
    { id: "missing_features", label: "It's missing features I need" },
    { id: "other", label: "Other" },
  ];

  return (
    <div className="ms-overlay" onClick={onClose}>
      <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ms-close-btn" onClick={onClose}>&times;</button>

        {!cancelled ? (
          <>
            {step === 1 && (
              <div className="ms-step">
                <h2>Manage Subscription</h2>
                <div className="ms-plan-details">
                  <div className="ms-detail-row">
                    <span>Plan</span>
                    <strong>{subscription?.plan || "PRO"}</strong>
                  </div>
                  <div className="ms-detail-row">
                    <span>Status</span>
                    <strong className={`status-${subscription?.status?.toLowerCase()}`}>
                      {subscription?.status}
                    </strong>
                  </div>
                  <div className="ms-detail-row">
                    <span>Renewal Date</span>
                    <strong>{formatDate(subscription?.currentPeriodEnd)}</strong>
                  </div>
                </div>

                <div className="ms-actions">
                  <button className="ms-btn-secondary" onClick={() => setStep(2)}>
                    Cancel Subscription
                  </button>
                  <button className="ms-btn-primary" onClick={onClose}>
                    Keep Premium Access
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="ms-step">
                <h2>Why are you leaving?</h2>
                <p>We're sorry to see you go. Help us improve Learnlia.</p>
                <div className="ms-survey">
                  {SURVEY_OPTIONS.map((opt) => (
                    <label key={opt.id} className="ms-survey-option">
                      <input
                        type="radio"
                        name="reason"
                        value={opt.id}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <div className="ms-actions">
                  <button className="ms-btn-primary" onClick={() => setStep(3)} disabled={!reason}>
                    Continue
                  </button>
                  <button className="ms-btn-ghost" onClick={() => setStep(1)}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="ms-step">
                <div className="ms-warning-icon">👋</div>
                <h2>Are you sure?</h2>
                <div className="ms-emotional-copy">
                  <p>By cancelling, <strong>you are leaving Learnlia and your structured learning system.</strong></p>
                  <p>You'll lose access to:</p>
                  <ul>
                    <li>🔥 AI-Powered Quizzes</li>
                    <li>📊 Personalized Velocity Tracking</li>
                    <li>🏆 Verified Track Courses</li>
                  </ul>
                  <p className="ms-clarity">
                    Your plan will remain active until <strong>{formatDate(subscription?.currentPeriodEnd)}</strong>. No more charges will be made.
                  </p>
                </div>
                <div className="ms-actions">
                  <button className="ms-btn-danger" onClick={handleCancelClick} disabled={loading}>
                    {loading ? "Processing..." : "Confirm Cancellation"}
                  </button>
                  <button className="ms-btn-primary" onClick={onClose}>
                    Stay with Learnlia
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="ms-step ms-success">
            <div className="ms-success-icon">✅</div>
            <h2>Subscription Cancelled</h2>
            <p>Your subscription has been cancelled. You will retain PRO access until {formatDate(subscription?.currentPeriodEnd)}.</p>
            <button className="ms-btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSubscriptionModal;
